import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Checkbox } from '../../components/ui/checkbox';
import { CalendarDays, Plus, Copy, RefreshCw, Users, Clock, ArrowLeft, Video, ExternalLink, Eye, Trash2, MoreVertical, Pencil, XCircle, ChevronDown, BookOpen, Loader2, CheckCircle } from 'lucide-react';
import { formatDate, formatTime, formatTimeLocal, getTimezoneAbbr, isIST, convertISTToLocal, getStatusColor, getBatchTypeLabel, getCourseTypeLabel, getInitials, getDurationLabel, toISTDateStr } from '../../lib/utils';
import { toast } from 'sonner';
import BulkEditScopeModal from '../../components/BulkEditScopeModal';
import TimezoneHeader from '../../components/TimezoneHeader';
import UserDetailModal from '../../components/UserDetailModal';
import SessionDetailModal from '../../components/SessionDetailModal';

export default function BatchDetail() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminOnBehalf = user?.role === 'admin' || user?.role === 'superadmin';
  const [batch, setBatch] = useState(null);
  const [totals, setTotals] = useState({ upcoming: 0, past: 0, past_loaded: 0 });
  const displayNameRef = useRef(null);  // V61.5 — learner-facing batch name
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('sessions');
  const [cancelDialog, setCancelDialog] = useState({ open: false, session: null, note: '', creditsToDeduct: 0 });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, session: null, scope: 'this_session' });
  const [editScopeSession, setEditScopeSession] = useState(null);
  const [attendanceDialog, setAttendanceDialog] = useState({ open: false, session: null, data: null });
  const [learnerSearch, setLearnerSearch] = useState('');
  const [learnerSearchResults, setLearnerSearchResults] = useState([]);
  const [sessionFilter, setSessionFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);
  // V14.5 — bulk-assign-course / bulk-assign-batch state
  const [selectedLearnerIds, setSelectedLearnerIds] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignBatchOpen, setBulkAssignBatchOpen] = useState(false);

  const [creditDialog, setCreditDialog] = useState({ open: false });
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditAction, setCreditAction] = useState('add');
  const [learnerModal, setLearnerModal] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [bufferSettings, setBufferSettings] = useState({ instructor_start_buffer: 10, learner_join_buffer: 5 });
  // V14.3 — Booked-learners modal for group sessions
  const [bookingsDialog, setBookingsDialog] = useState({ open: false, session: null, data: null, loading: false });
  const [bookingsTab, setBookingsTab] = useState('booked');

  const openBookingsDialog = async (session) => {
    setBookingsTab('booked');
    setBookingsDialog({ open: true, session, data: null, loading: true });
    try {
      const res = await api.get(`/api/sessions/${session.id}/booked-learners`);
      setBookingsDialog({ open: true, session, data: res.data, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bookings');
      setBookingsDialog({ open: false, session: null, data: null, loading: false });
    }
  };

  const fetchData = () => {
    return Promise.all([
      api.get(`/api/batches/${batchId}`),
      // V14.7b — Fetch upcoming+live and past separately to avoid limit cutoff.
      // Batches with 200+ completed sessions were hiding upcoming ones.
      api.get('/api/sessions', { params: { batch_id: batchId, status: 'upcoming', limit: 500 } }),
      api.get('/api/sessions', { params: { batch_id: batchId, status: 'live', limit: 20 } }),
      api.get('/api/sessions', { params: { batch_id: batchId, status: 'past', limit: 500 } }),
      api.get('/api/settings/buffers'),
    ]).then(([bRes, upRes, liveRes, pastRes, bufRes]) => {
      setBatch(bRes.data);
      const upcoming = upRes.data.sessions || [];
      const live = liveRes.data.sessions || [];
      const past = pastRes.data.sessions || [];
      setSessions([...live, ...upcoming, ...past]);
      // V70 — the API caps each list, so trust its `total` for the counts instead of the
      // array length. Previously both counts read exactly "500" with no hint that the list
      // was truncated, which is how 5-10 Oct went missing without anyone noticing.
      setTotals({
        upcoming: upRes.data.total ?? upcoming.length,
        past: pastRes.data.total ?? past.length,
        past_loaded: past.length,
      });
      setBufferSettings(bufRes.data);
    });
  };

  useEffect(() => { fetchData().finally(() => setLoading(false)); }, [batchId]);

  // Auto-refresh every 30s when a session is live, every 15s when close to start time
  useEffect(() => {
    const hasLive = sessions.some(s => s.status === 'live');
    const hasUpcomingSoon = sessions.some(s => {
      if (s.status !== 'upcoming') return false;
      const dt = new Date(`${s.scheduled_date}T${s.scheduled_time}:00`);
      return (dt - new Date()) / 60000 <= 15; // within 15 min
    });
    if (!hasLive && !hasUpcomingSoon) return;
    const interval = setInterval(() => { fetchData(); }, hasLive ? 15000 : 30000);
    return () => clearInterval(interval);
  }, [sessions]);

  const copyPublicLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${batch?.public_slug}`);
    toast.success('Public link copied!');
  };

  const resetPublicLink = async () => {
    if (!window.confirm(
      'Reset the public link for this batch?\n\n'
      + 'The current link stops working IMMEDIATELY. Anyone who already has it — including every '
      + 'mother in the WhatsApp group — will see "Link Not Found" until you share the new link.\n\n'
      + 'Only do this if the link has leaked outside the batch.'
    )) return;
    const typed = window.prompt('Type RESET to confirm you want to break the current link:');
    if ((typed || '').trim().toUpperCase() !== 'RESET') {
      toast.info('Link left as it is');
      return;
    }
    const res = await api.post(`/api/batches/${batchId}/reset-public-link`);
    setBatch(prev => ({
      ...prev,
      public_slug: res.data.public_slug,
      public_link_reset_at: res.data.reset_at,
      public_link_reset_by: res.data.reset_by,
    }));
    toast.success('New link generated — share it now, the old one is dead');
  };

  const handleCancel = async () => {
    try {
      await api.post(
        `/api/sessions/${cancelDialog.session.id}/cancel?reason=${encodeURIComponent(cancelDialog.note || 'Canceled by admin')}`,
        { credits_to_deduct: parseInt(cancelDialog.creditsToDeduct, 10) || 0 }
      );
      toast.success('Session canceled');
      setCancelDialog({ open: false, session: null, note: '', creditsToDeduct: 0 });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleBulkDelete = async () => {
    try {
      const res = await api.post(`/api/sessions/${deleteDialog.session.id}/bulk-delete`, { scope: deleteDialog.scope });
      toast.success(res.data.message);
      setDeleteDialog({ open: false, session: null, scope: 'this_session' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewAttendance = async (session) => {
    try {
      const res = await api.get(`/api/zoom/session-attendance/${session.id}`);
      setAttendanceDialog({ open: true, session, data: res.data });
    } catch { toast.error('Failed to load attendance'); }
  };

  // Auto-refresh live attendance every 15s while modal is open on a live session
  useEffect(() => {
    if (!attendanceDialog.open || !attendanceDialog.session || attendanceDialog.session.status !== 'live') return;
    const interval = setInterval(() => {
      api.get(`/api/zoom/session-attendance/${attendanceDialog.session.id}`)
        .then((res) => setAttendanceDialog((d) => d.open ? { ...d, data: res.data } : d))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [attendanceDialog.open, attendanceDialog.session]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!batch) return <div className="text-center py-12"><p className="text-muted-foreground">Batch not found</p></div>;

  const todayStr = toISTDateStr(new Date());
  
  // Filter upcoming: only today + future in IST. Time comparison also in IST.
  const upcomingSessions = sessions.filter(s => {
    if (s.status === 'live') return true;
    if (s.status !== 'upcoming') return false;
    // Include if date is after today (IST)
    if (s.scheduled_date > todayStr) return true;
    if (s.scheduled_date === todayStr) {
      // Include today's sessions that haven't ended yet (in IST, not browser local time)
      const [h, m] = (s.scheduled_time || '0:0').split(':').map(Number);
      const sessionEndMinutes = h * 60 + m + (s.duration_minutes || 60);
      // Current time in IST
      const nowIST = new Date(Date.now() + 5.5 * 60 * 60000);
      const nowISTMinutes = nowIST.getUTCHours() * 60 + nowIST.getUTCMinutes();
      return sessionEndMinutes > nowISTMinutes;
    }
    return false;
  }).sort((a, b) => (a.scheduled_date + a.scheduled_time).localeCompare(b.scheduled_date + b.scheduled_time));
  
  const pastSessions = sessions.filter(s => s.status !== 'upcoming' && s.status !== 'live')
    .sort((a, b) => (b.scheduled_date + b.scheduled_time).localeCompare(a.scheduled_date + a.scheduled_time));
  const liveSession = sessions.find(s => s.status === 'live');

  // Apply session filter
  const filterSessions = (sessionList) => {
    if (sessionFilter === 'all') return sessionList;
    const now = new Date();
    const getFilterDate = () => {
      if (sessionFilter === '7days') return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      if (sessionFilter === '30days') return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      return null;
    };
    const filterDate = getFilterDate();
    if (!filterDate) return sessionList;
    return sessionList.filter(s => {
      const sDate = new Date(s.scheduled_date);
      return sDate >= now && sDate <= filterDate;
    });
  };
  const displaySessions = showPast ? pastSessions : filterSessions(upcomingSessions);

  // Helper: compute end time
  const getEndTime = (time, duration, dateStr) => {
    const localTime = isIST() ? time : convertISTToLocal(time, dateStr);
    const [h, m] = (localTime || '0:0').split(':').map(Number);
    const endMin = h * 60 + m + (duration || 60);
    const eh = Math.floor(endMin / 60) % 24;
    const em = endMin % 60;
    return `${eh > 12 ? eh - 12 : eh || 12}:${String(em).padStart(2, '0')} ${eh >= 12 ? 'PM' : 'AM'}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/batches')}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{batch.name}</h1>
            {liveSession && <Badge className="bg-rose-500 text-white border-0 animate-pulse">Live</Badge>}
            <Badge variant="outline" className={batch.batch_type === 'one_on_one' ? 'bg-teal-50 text-teal-800 border-teal-200' : 'bg-orange-50 text-orange-800 border-orange-200'}>{getBatchTypeLabel(batch.batch_type)}</Badge>
            <Badge variant="secondary">{getCourseTypeLabel(batch.course_type)}</Badge>
          </div>
        </div>
        <Button onClick={() => navigate(`/admin/batches/${batchId}/schedule`)} data-testid="schedule-sessions-button">
          <Plus className="h-4 w-4 mr-2" />Schedule Sessions
        </Button>
      </div>

      {/* Session in Progress Banner */}
      {liveSession && (
        <Card className="rounded-2xl border-rose-200 bg-rose-50/50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-rose-500 flex items-center justify-center">
                  <Video className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-rose-900">Session in progress</p>
                  <p className="text-xs text-rose-700">{liveSession.title} — HOSTED BY {(liveSession.instructor_name || 'Instructor').toUpperCase()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => viewAttendance(liveSession)} data-testid="live-attendance-button">
                  Attendance
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await api.get(`/api/sessions/${liveSession.id}/link`);
                    const url = res.data.zoom_join_url || res.data.zoom_join_web_url;
                    if (url) window.open(url, '_blank');
                    else toast.error('Join URL not available');
                  } catch { toast.error('Failed to get join link'); }
                }}>
                  Join
                </Button>
                <Button size="sm" className="bg-slate-800 hover:bg-slate-900 text-white" onClick={async () => {
                  try {
                    const res = await api.get(`/api/sessions/${liveSession.id}/link`);
                    if (res.data.zoom_start_url) {
                      window.open(res.data.zoom_start_url, '_blank');
                    }
                    else toast.error(res.data.start_blocked_reason || 'Cannot start now');
                  } catch { toast.error('Failed to get host link'); }
                }}>
                  Join as Host
                </Button>
                <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="end-no-show-button" onClick={async () => {
                  try {
                    const res = await api.post(`/api/sessions/${liveSession.id}/end-no-show`);
                    toast.success(res.data.message || 'Ended as no-show');
                    fetchData();
                  } catch (err) {
                    const d = err.response?.data?.detail;
                    toast.error(d?.message || d || 'Failed to end as no-show');
                  }
                }}>
                  End as No-show
                </Button>
                <Button size="sm" variant="destructive" onClick={async () => {
                  try {
                    await api.post(`/api/sessions/${liveSession.id}/end`);
                    toast.success('Session ended');
                    fetchData();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to end'); }
                }}>
                  End
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Upcoming</p>
          <p className="text-2xl font-semibold mt-1" data-testid="batch-kpi-upcoming">{totals.upcoming || upcomingSessions.length}</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Past Sessions</p>
          <p className="text-2xl font-semibold mt-1" data-testid="batch-kpi-past">{totals.past || pastSessions.length}</p>
        </CardContent></Card>
        {batch.batch_type === 'group' && (
          <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Learners</p>
            <p className="text-2xl font-semibold mt-1">{batch.learner_count}</p>
            <button className="text-[10px] text-primary mt-0.5 hover:underline" onClick={() => setLearnerModal(true)}>View Learners</button>
          </CardContent></Card>
        )}
        {batch.batch_type === 'one_on_one' && (
          <>
            {/* Learner identity card — click to open the learner's detail modal */}
            {(() => {
              const learner = batch.learners?.[0];
              if (!learner) {
                return (
                  <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Learner</p>
                    <p className="text-sm text-muted-foreground mt-2">Not assigned yet</p>
                  </CardContent></Card>
                );
              }
              const tri = learner.pregnancy_profile?.trimester;
              const risk = learner.pregnancy_profile?.risk_level;
              const dDate = learner.pregnancy_profile?.delivery_date;
              const dMode = learner.pregnancy_profile?.delivery_mode;
              const isPostnatal = batch.course_type === 'postnatal';
              return (
                <Card
                  className="rounded-2xl cursor-pointer hover:shadow-md hover:border-primary/40 transition-all"
                  onClick={() => setDetailUserId(learner.id)}
                  data-testid="batch-1on1-learner-card"
                >
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Learner</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarFallback className="text-[11px] bg-primary/10 text-primary">{getInitials(learner.name)}</AvatarFallback>
                      </Avatar>
                      <p className="text-base font-semibold truncate" title={learner.name}>{learner.name}</p>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {isPostnatal ? (
                        <>
                          {dDate && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">{formatDate(dDate)}</Badge>}
                          {dMode && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 h-4 ${(dMode === 'vaginal' || dMode === 'natural') ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-rose-50 text-rose-800 border-rose-200'}`}
                            >
                              {(dMode === 'vaginal' || dMode === 'natural') ? 'Vaginal birth' : 'C-section'}
                            </Badge>
                          )}
                          {!dDate && !dMode && <span className="text-[10px] text-amber-700">⏳ Awaiting delivery details</span>}
                        </>
                      ) : (
                        <>
                          {tri && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">T{tri}</Badge>}
                          {risk && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 h-4 capitalize ${risk === 'high' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}
                            >
                              {risk}-risk
                            </Badge>
                          )}
                          {!tri && !risk && <span className="text-[10px] text-muted-foreground">No profile yet</span>}
                        </>
                      )}
                    </div>
                    <p className="text-[10px] text-primary mt-1.5 font-medium">View profile →</p>
                  </CardContent>
                </Card>
              );
            })()}
            <Card className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={async () => {
              try { const res = await api.get(`/api/credits/${batchId}/history`); setCreditHistory(res.data.history || []); } catch {}
              setCreditDialog({ open: true });
            }}>
              <CardContent className="pt-5 pb-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Credits</p>
                <p className="text-2xl font-semibold mt-1">{batch.credits_remaining}/{batch.credits_total}</p>
                <p className="text-[10px] text-primary mt-0.5">Manage</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          {batch.batch_type === 'group' && <TabsTrigger value="learners">Learners</TabsTrigger>}
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Sessions Tab — Wise-style cards */}
        <TabsContent value="sessions" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button variant={!showPast ? 'default' : 'outline'} size="sm" onClick={() => setShowPast(false)} data-testid="batch-tab-upcoming">
                Upcoming <span className="ml-1 text-xs opacity-70">{totals.upcoming || upcomingSessions.length}</span>
              </Button>
              <Button variant={showPast ? 'default' : 'outline'} size="sm" onClick={() => setShowPast(true)} data-testid="batch-tab-past">
                Past <span className="ml-1 text-xs opacity-70">{totals.past || pastSessions.length}</span>
              </Button>
            </div>
            {!showPast && (
              <div className="flex gap-1">
                {[{v:'all',l:'All'},{v:'7days',l:'7 Days'},{v:'30days',l:'30 Days'}].map(f => (
                  <Button key={f.v} variant={sessionFilter === f.v ? 'secondary' : 'ghost'} size="sm" className="text-xs h-7" onClick={() => setSessionFilter(f.v)}>{f.l}</Button>
                ))}
              </div>
            )}
          </div>
          <TimezoneHeader />
          {sessions.length === 0 ? (
            <Card className="rounded-2xl"><CardContent className="pt-6 text-center py-12">
              <CalendarDays className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No sessions scheduled yet</p>
              <Button variant="outline" className="mt-3" onClick={() => navigate(`/admin/batches/${batchId}/schedule`)}>Schedule Sessions</Button>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {showPast && totals.past > totals.past_loaded && (
                <p className="text-xs text-muted-foreground px-1" data-testid="batch-past-truncated-note">
                  Showing the {totals.past_loaded} most recent of {totals.past} past sessions.
                </p>
              )}
              {displaySessions.map(s => {
                const d = new Date(s.scheduled_date + 'T00:00:00');
                // Check if session is within start window (20 min before = show greyed, 10 min = active)
                const sessionDateTime = new Date(`${s.scheduled_date}T${s.scheduled_time}:00`);
                const now = new Date();
                const minutesUntilStart = (sessionDateTime - now) / (1000 * 60);
                const sessionEndTime = new Date(sessionDateTime.getTime() + (s.duration_minutes || 60) * 60000);
                const canShowJoin = minutesUntilStart <= (bufferSettings.instructor_start_buffer || 10) + 10 && now < sessionEndTime;
                const canStart = minutesUntilStart <= (bufferSettings.instructor_start_buffer || 10) && now < sessionEndTime;
                const isNextUp = !showPast && displaySessions.indexOf(s) === 0 && s.status === 'upcoming';
                // Restart: session completed early and restart window still open
                const canRestart = s.status === 'completed' && s.restart_allowed_until && new Date() < new Date(s.restart_allowed_until);
                return (
                  <div key={s.id} className="flex items-center justify-between p-4 rounded-xl border hover:bg-muted/20 transition-colors cursor-pointer" onClick={(e) => { if (!e.target.closest('button') && !e.target.closest('[role="menu"]')) { setSelectedSession(s); } }}>
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[48px] bg-primary/10 rounded-xl px-2 py-1.5">
                        <p className="text-[10px] text-primary uppercase font-medium">{d.toLocaleDateString('en', { month: 'short' })}</p>
                        <p className="text-xl font-semibold text-primary leading-tight">{d.getDate()}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">{s.title}</p>
                          {s.zoom_join_url && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[9px]">Zoom</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground uppercase">HOSTED BY {(s.instructor_name || 'TBD').toUpperCase()}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <Clock className="h-3 w-3" />
                          <span>{d.toLocaleDateString('en', { weekday: 'short' })} {formatTimeLocal(s.scheduled_time, s.scheduled_date)} - {getEndTime(s.scheduled_time, s.duration_minutes, s.scheduled_date)}</span>
                          <span>· {s.conducted_minutes ? `${Math.round(s.conducted_minutes)}m actual` : `${s.duration_minutes}m`}</span>
                        </div>
                        <Badge variant="outline" className={`${getStatusColor(s.status)} text-[10px] mt-1`}>
                          {s.status === 'live' ? <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span> : s.status}
                        </Badge>
                        {/* V14.3 — Booked count for group sessions only (1:1 batches show learner card elsewhere). */}
                        {batch?.batch_type === 'group' && (
                          <Badge
                            variant="outline"
                            className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] mt-1 ml-1.5 cursor-pointer hover:bg-indigo-100"
                            onClick={(e) => { e.stopPropagation(); openBookingsDialog(s); }}
                            data-testid={`session-${s.id}-bookings-badge`}
                          >
                            <Users className="h-3 w-3 mr-1" />
                            {(s.booked_learners?.length || 0)} booked
                          </Badge>
                        )}
                        {/* V66 (founder) — past sessions show who actually ATTENDED next to booked. */}
                        {batch?.batch_type === 'group' && ['completed', 'missed'].includes(s.status) && (
                          <Badge
                            variant="outline"
                            className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] mt-1 ml-1.5 cursor-pointer hover:bg-emerald-100"
                            onClick={(e) => { e.stopPropagation(); setSelectedSession(s); }}
                            data-testid={`session-${s.id}-attended-badge`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {s.attended_count ?? s.attendance_count ?? 0} attended
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Live: prominent "Live" pill (matches the in-progress banner above —
                          which already has Join / Join as Host / End buttons, so duplicating
                          a Join dropdown here is noisy). Click navigates the user to scroll
                          up to those primary actions. */}
                      {s.status === 'live' && (
                        <Badge
                          className="bg-rose-100 text-rose-700 border-rose-200 px-2.5 py-1 text-[11px] font-semibold flex items-center gap-1.5 hover:bg-rose-200 cursor-pointer"
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          data-testid={`session-${s.id}-live-indicator`}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                          Live now
                        </Badge>
                      )}
                      {/* Upcoming within start window: show Join dropdown */}
                      {s.status === 'upcoming' && canStart && s.zoom_join_url && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm"><Video className="h-3 w-3 mr-1" />Join<ChevronDown className="h-3 w-3 ml-1" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={async () => {
                              // For admin/superadmin who isn't the assigned instructor:
                              // hit /admin-start (issues a fresh start_url + audit-logs).
                              // For everyone else (the actual instructor), keep using /link
                              // which handles their ZAK-based host promotion path.
                              const isAssignedInstructor = String(s.instructor_id || '') === String(user?.id || user?.user_id || '');
                              const useAdminStart = isAdminOnBehalf && !isAssignedInstructor;
                              try {
                                if (useAdminStart) {
                                  const r = await api.post(`/api/sessions/${s.id}/admin-start`);
                                  if (r.data.start_url) {
                                    window.open(r.data.start_url, '_blank');
                                    toast.info('Opening Zoom — hosting on behalf of the instructor.');
                                  } else {
                                    toast.error('Could not get host link.');
                                  }
                                  return;
                                }
                                const res = await api.get(`/api/sessions/${s.id}/link`);
                                if (res.data.zoom_start_url) {
                                  window.open(res.data.zoom_start_url, '_blank');
                                  toast.info('Opening Zoom... Session will show as Live once you join.');
                                } else {
                                  toast.error(res.data.start_blocked_reason || 'Cannot start yet');
                                }
                              } catch (err) {
                                const detail = err?.response?.data?.detail;
                                const msg = (detail && typeof detail === 'object' && detail.message) ? detail.message : (typeof detail === 'string' ? detail : 'Failed to get host link');
                                toast.error(msg);
                              }
                            }} data-testid={`session-${s.id}-start-as-host`}>
                              <Video className="h-4 w-4 mr-2" />{isAdminOnBehalf ? 'Start as Host' : 'Join as Host'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              try {
                                const res = await api.get(`/api/sessions/${s.id}/link`);
                                const url = res.data.zoom_join_url || res.data.zoom_join_web_url;
                                if (url) window.open(url, '_blank');
                                else toast.error('Join URL not available');
                              } catch { toast.error('Failed to get join link'); }
                            }}>
                              <Users className="h-4 w-4 mr-2" />Join as Learner
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {/* Upcoming but not yet in start window — show greyed button */}
                      {s.status === 'upcoming' && canShowJoin && !canStart && (
                        <Button size="sm" disabled className="opacity-30"><Video className="h-3 w-3 mr-1" />Starting soon</Button>
                      )}
                      {/* Restart button for sessions ended early */}
                      {canRestart && (
                        <Button size="sm" variant="outline" onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await api.get(`/api/sessions/${s.id}/link`);
                            if (res.data.zoom_start_url) {
                              window.open(res.data.zoom_start_url, '_blank');
                              toast.info('Restarting... Session will show as Live once you join.');
                            }
                            else toast.error(res.data.start_blocked_reason || 'Restart window expired');
                          } catch { toast.error('Failed to get restart link'); }
                        }}>
                          <RefreshCw className="h-3 w-3 mr-1" />Restart
                        </Button>
                      )}
                      {s.status === 'upcoming' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(s.id); toast.success('Session ID copied!'); }}>
                              <Copy className="h-4 w-4 mr-2" />Copy Session ID
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditScopeSession(s)}>
                              <Pencil className="h-4 w-4 mr-2" />Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, session: s, scope: 'this_session' })} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />Delete
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setCancelDialog({ open: true, session: s, note: '' })} className="text-destructive">
                              <XCircle className="h-4 w-4 mr-2" />Cancel session
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {s.status !== 'upcoming' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(s.id); toast.success('Session ID copied!'); }}>
                              <Copy className="h-4 w-4 mr-2" />Copy Session ID
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              try {
                                const res = await api.get(`/api/sessions/${s.id}/diagnose`);
                                setDiagnosticData(res.data);
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load diagnostics'); }
                            }}>
                              <Eye className="h-4 w-4 mr-2" />Diagnose Session
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Learners Tab */}
        <TabsContent value="learners" className="mt-4">
          <Card className="rounded-2xl"><CardContent className="pt-5">
            {/* Add Learner Section */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 relative">
                <Input
                  placeholder="Search learner by name or email..."
                  value={learnerSearch}
                  onChange={async (e) => {
                    setLearnerSearch(e.target.value);
                    if (e.target.value.length >= 2) {
                      try {
                        const res = await api.get('/api/users', { params: { search: e.target.value, role: 'learner', limit: 10 } });
                        setLearnerSearchResults(res.data.users || []);
                      } catch {}
                    } else {
                      setLearnerSearchResults([]);
                    }
                  }}
                  data-testid="add-learner-search"
                />
                {learnerSearchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 bg-background border rounded-xl shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {learnerSearchResults.map(l => {
                      const alreadyAdded = (batch.learner_ids || []).includes(l.id);
                      return (
                        <div key={l.id} className={`flex items-center justify-between px-3 py-2 hover:bg-muted/50 cursor-pointer ${alreadyAdded ? 'opacity-50' : ''}`}
                          onClick={async () => {
                            if (alreadyAdded) return;
                            try {
                              await api.post(`/api/batches/${batchId}/add-learner?learner_id=${l.id}`);
                              toast.success(`${l.name} added to batch`);
                              setLearnerSearch('');
                              setLearnerSearchResults([]);
                              fetchData();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
                          }}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px] bg-primary/10 text-primary">{getInitials(l.name)}</AvatarFallback></Avatar>
                            <div>
                              <p className="text-sm font-medium">{l.name}</p>
                              <p className="text-xs text-muted-foreground">{l.email}</p>
                            </div>
                          </div>
                          {alreadyAdded ? <Badge variant="outline" className="text-[9px]">Already added</Badge> : <Button size="sm" variant="ghost" className="text-xs">Add</Button>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {batch.learners?.length > 0 ? (
              <>
                {/* V14.5 — Bulk-assign toolbar (appears when ≥1 learner selected) */}
                {selectedLearnerIds.size > 0 && (
                  <div className="flex items-center justify-between rounded-xl border bg-rose-50/60 px-3 py-2 mb-3" data-testid="bulk-assign-toolbar">
                    <p className="text-xs">
                      <strong>{selectedLearnerIds.size}</strong> learner{selectedLearnerIds.size === 1 ? '' : 's'} selected
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedLearnerIds(new Set())}
                        data-testid="bulk-clear-selection-btn"
                      >
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setBulkAssignOpen(true)}
                        data-testid="bulk-assign-course-btn"
                      >
                        <BookOpen className="h-3.5 w-3.5 mr-1.5" />
                        Assign Course
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setBulkAssignBatchOpen(true)}
                        data-testid="bulk-assign-batch-btn"
                      >
                        <Users className="h-3.5 w-3.5 mr-1.5" />
                        Assign Batch
                      </Button>
                    </div>
                  </div>
                )}

                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedLearnerIds.size > 0 && selectedLearnerIds.size === batch.learners.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedLearnerIds(new Set(batch.learners.map(l => l.id)));
                          } else {
                            setSelectedLearnerIds(new Set());
                          }
                        }}
                        aria-label="Select all learners"
                        data-testid="learner-select-all"
                      />
                    </TableHead>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead>
                  {batch.course_type === 'postnatal' ? (
                    <><TableHead>Delivery Date</TableHead><TableHead>Delivery Type</TableHead></>
                  ) : (
                    <><TableHead>Trimester</TableHead><TableHead>Risk</TableHead></>
                  )}
                </TableRow></TableHeader>
                <TableBody>
                  {batch.learners.map(l => (
                    <TableRow
                      key={l.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setDetailUserId(l.id)}
                      data-testid={`learner-row-${l.id}`}
                      data-state={selectedLearnerIds.has(l.id) ? 'selected' : undefined}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedLearnerIds.has(l.id)}
                          onCheckedChange={(checked) => {
                            setSelectedLearnerIds(prev => {
                              const next = new Set(prev);
                              if (checked) next.add(l.id); else next.delete(l.id);
                              return next;
                            });
                          }}
                          aria-label={`Select ${l.name}`}
                          data-testid={`learner-select-${l.id}`}
                        />
                      </TableCell>
                      <TableCell className="flex items-center gap-2">
                        <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{getInitials(l.name)}</AvatarFallback></Avatar>
                        <span className="hover:text-primary transition-colors">{l.name}</span>
                      </TableCell>
                      <TableCell>{l.email}</TableCell>
                      {batch.course_type === 'postnatal' ? (
                        <>
                          <TableCell>
                            {l.pregnancy_profile?.delivery_date
                              ? formatDate(l.pregnancy_profile.delivery_date)
                              : <span className="text-muted-foreground text-xs">— pending</span>}
                          </TableCell>
                          <TableCell>
                            {l.pregnancy_profile?.delivery_mode ? (
                              <Badge
                                variant="outline"
                                className={(l.pregnancy_profile.delivery_mode === 'vaginal' || l.pregnancy_profile.delivery_mode === 'natural')
                                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                  : 'bg-rose-50 text-rose-800 border-rose-200'}
                              >
                                {(l.pregnancy_profile.delivery_mode === 'vaginal' || l.pregnancy_profile.delivery_mode === 'natural') ? 'Vaginal birth' : 'C-section'}
                              </Badge>
                            ) : <span className="text-muted-foreground text-xs">— pending</span>}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{l.pregnancy_profile?.trimester ? `T${l.pregnancy_profile.trimester}` : '-'}</TableCell>
                          <TableCell>
                            {l.pregnancy_profile?.risk_level ? (
                              <Badge variant="outline" className={l.pregnancy_profile.risk_level === 'high' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}>{l.pregnancy_profile.risk_level}</Badge>
                            ) : '-'}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
                </Table>
              </>
            ) : (
              <div className="text-center py-8"><Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No learners in this batch</p></div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          <Card className="rounded-2xl"><CardContent className="pt-5 space-y-6">
            <div>
              <h3 className="text-sm font-medium mb-3">Public Links</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Learner Link (waiting room)</label>
                  <div className="flex items-center gap-2">
                    <Input value={`${window.location.origin}/join/${batch.public_slug}`} readOnly className="flex-1 font-mono text-sm" data-testid="public-link-learner" />
                    <Button variant="outline" size="icon" onClick={copyPublicLink} data-testid="copy-learner-link"><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Joiners via public link will be marked as 'Zoom' in attendance.</p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2 text-xs"
                      onClick={resetPublicLink}
                      data-testid="reset-learner-link"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />Reset link (breaks the old one)
                    </Button>
                    {batch.public_link_reset_at && (
                      <span className="text-[10px] text-muted-foreground" data-testid="public-link-reset-meta">
                        Last reset {formatDate(batch.public_link_reset_at)}
                        {batch.public_link_reset_by ? ` by ${batch.public_link_reset_by}` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Instructor Link (co-host)</label>
                  <div className="flex items-center gap-2">
                    <Input value={`${window.location.origin}/host/${batch.public_slug}`} readOnly className="flex-1 font-mono text-sm" data-testid="public-link-instructor" />
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/host/${batch.public_slug}`); toast.success('Instructor link copied!'); }} data-testid="copy-instructor-link"><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Instructor joins as co-host via this link.</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border p-3 space-y-2">
              <div>
                <p className="text-sm font-medium">Learner-facing name</p>
                <p className="text-xs text-muted-foreground">
                  What moms see in the app and in notifications. Leave blank to show the batch
                  name as it is here — <span className="font-medium">{batch.name}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  defaultValue={batch.display_name || ''}
                  placeholder={batch.name}
                  onChange={(e) => { displayNameRef.current = e.target.value; }}
                  className="flex-1"
                  data-testid="batch-display-name-input"
                />
                <Button
                  variant="outline"
                  data-testid="batch-display-name-save"
                  onClick={async () => {
                    const val = (displayNameRef.current ?? batch.display_name ?? '').trim();
                    await api.put(`/api/batches/${batchId}`, { display_name: val });
                    setBatch((prev) => ({ ...prev, display_name: val }));
                    toast.success(val ? 'Learners will now see this name' : 'Reverted to the batch name');
                  }}
                >Save</Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium">Share Recordings</p><p className="text-xs text-muted-foreground">Allow learners to view past recordings</p></div>
              <Switch checked={batch.share_recordings} onCheckedChange={async (val) => { await api.put(`/api/batches/${batchId}`, { share_recordings: val }); setBatch(prev => ({...prev, share_recordings: val})); }} />
            </div>
            {batch.batch_type === 'one_on_one' && (
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">Allow Learner Cancellation</p><p className="text-xs text-muted-foreground">1:1 learners can cancel sessions</p></div>
                <Switch checked={batch.allow_learner_cancel} onCheckedChange={async (val) => { await api.put(`/api/batches/${batchId}`, { allow_learner_cancel: val }); setBatch(prev => ({...prev, allow_learner_cancel: val})); }} />
              </div>
            )}

            {/* Max Capacity (Group batches) */}
            {batch.batch_type === 'group' && (
              <div className="pt-2 border-t">
                <h3 className="text-sm font-medium mb-1 flex items-center gap-2"><Users className="h-4 w-4" />Session Capacity</h3>
                <p className="text-xs text-muted-foreground mb-3">Maximum number of learners who can book each session in this batch. When full, new bookings go to waitlist.</p>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    className="w-24"
                    value={batch.max_capacity || 15}
                    onChange={async (e) => {
                      const val = parseInt(e.target.value) || 15;
                      try {
                        await api.put(`/api/batches/${batchId}`, { max_capacity: val });
                        setBatch(prev => ({...prev, max_capacity: val}));
                        toast.success(`Max capacity set to ${val}`);
                      } catch { toast.error('Failed to update'); }
                    }}
                    data-testid="max-capacity-input"
                  />
                  <span className="text-sm text-muted-foreground">learners per session</span>
                </div>
              </div>
            )}

            {/* Batch Tags — V14.2 */}
            <div className="pt-2 border-t">
              <h3 className="text-sm font-medium mb-3">Batch Tags</h3>
              <div className="grid grid-cols-2 gap-3">
                {/* V14.2 — Risk Level is hidden for postnatal & TTC batches.
                    It's a pregnancy-specific OB tag. */}
                {batch.course_type === 'prenatal' && (
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Risk Level</label>
                    <select className="flex h-9 w-full rounded-lg border bg-background px-3 text-sm" value={batch.risk_level || 'low'}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBatch(prev => ({...prev, risk_level: val}));
                        api.put(`/api/batches/${batchId}`, { risk_level: val }).then(() => toast.success('Risk level updated'));
                      }}>
                      <option value="low">Low Risk</option>
                      <option value="high">High Risk</option>
                    </select>
                  </div>
                )}
                {/* V14.2 — Course Type / Stage is LOCKED post-creation.
                    Backend rejects the change with COURSE_TYPE_LOCKED.
                    Show as read-only badge with explainer copy. */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Stage</label>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-primary text-primary-foreground border-0 capitalize" data-testid="batch-stage-badge">
                      {batch.course_type === 'ttc' ? 'TTC' : batch.course_type}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      Cannot be changed after creation
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* V14.2 — Unlock Rule */}
            <div className="pt-2 border-t">
              <h3 className="text-sm font-medium mb-1 flex items-center gap-2">Unlock Rule</h3>
              <p className="text-xs text-muted-foreground mb-3">
                When sales assigns this batch to a learner: should it open instantly, or only at a specific pregnancy week?
              </p>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Strategy</label>
                  <select className="flex h-9 w-full rounded-lg border bg-background px-3 text-sm"
                    value={batch.unlock_rule?.type || 'immediate'}
                    onChange={(e) => {
                      const t = e.target.value;
                      const next = t === 'immediate' ? { type: 'immediate' } : { type: 'week_gte', week: batch.unlock_rule?.week || 32 };
                      setBatch(prev => ({...prev, unlock_rule: next}));
                      api.put(`/api/batches/${batchId}`, { unlock_rule: next }).then(() => toast.success('Unlock rule updated')).catch(() => toast.error('Failed to update'));
                    }}
                    data-testid="batch-unlock-strategy">
                    <option value="immediate">Immediate — open the moment access is granted</option>
                    <option value="week_gte">Week-gated — unlocks at pregnancy week N</option>
                  </select>
                </div>
                {batch.unlock_rule?.type === 'week_gte' && (
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Pregnancy week</label>
                    <input type="number" min={1} max={45} className="flex h-9 w-full rounded-lg border bg-background px-3 text-sm"
                      value={batch.unlock_rule?.week ?? 32}
                      onChange={(e) => {
                        const week = parseInt(e.target.value) || 1;
                        const next = { type: 'week_gte', week };
                        setBatch(prev => ({...prev, unlock_rule: next}));
                      }}
                      onBlur={() => {
                        api.put(`/api/batches/${batchId}`, { unlock_rule: batch.unlock_rule }).then(() => toast.success('Week saved')).catch(() => toast.error('Failed to update'));
                      }}
                      data-testid="batch-unlock-week" />
                  </div>
                )}
              </div>
            </div>


            {/* Recording Upload Settings */}
            <div className="pt-2 border-t">
              <h3 className="text-sm font-medium mb-1 flex items-center gap-2"><Video className="h-4 w-4" />Recording Upload (Bunny Stream)</h3>
              <p className="text-xs text-muted-foreground mb-4">Choose which Zoom recording views to upload to Bunny Stream for VOD playback. Speaker View is recommended as default to save costs.</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium" data-testid="recording-upload-speaker-label">Upload Speaker View</p>
                    <p className="text-xs text-muted-foreground">Active speaker or screen+speaker view (recommended)</p>
                  </div>
                  <Switch
                    data-testid="recording-upload-speaker-toggle"
                    checked={batch.recording_upload_speaker !== false}
                    onCheckedChange={async (val) => {
                      try {
                        await api.put(`/api/batches/${batchId}`, { recording_upload_speaker: val });
                        setBatch(prev => ({...prev, recording_upload_speaker: val}));
                        toast.success(val ? 'Speaker view upload enabled' : 'Speaker view upload disabled');
                      } catch (err) { toast.error('Failed to update'); }
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium" data-testid="recording-upload-gallery-label">Upload Gallery View</p>
                    <p className="text-xs text-muted-foreground">Gallery view with all participants (additional cost)</p>
                  </div>
                  <Switch
                    data-testid="recording-upload-gallery-toggle"
                    checked={batch.recording_upload_gallery === true}
                    onCheckedChange={async (val) => {
                      try {
                        await api.put(`/api/batches/${batchId}`, { recording_upload_gallery: val });
                        setBatch(prev => ({...prev, recording_upload_gallery: val}));
                        toast.success(val ? 'Gallery view upload enabled' : 'Gallery view upload disabled');
                      } catch (err) { toast.error('Failed to update'); }
                    }}
                  />
                </div>
              </div>
              {batch.recording_upload_speaker === false && batch.recording_upload_gallery !== true && (
                <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded-lg">Both views are disabled. Recordings will not be uploaded to Bunny Stream for this batch.</p>
              )}
            </div>

            {/* V14.2 — Danger zone: Archive batch (soft-delete). Sessions
                cancel, learners lose visibility, but doc preserved for
                restore via /admin/batches/archived. */}
            <div className="pt-4 mt-4 border-t border-rose-100">
              <h3 className="text-sm font-medium mb-2 text-rose-700 flex items-center gap-2">
                <Trash2 className="h-4 w-4" />Danger zone
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                Archiving cancels all upcoming sessions and hides the batch from learners. The data is preserved — a super-admin can restore from the archive list, or hard-delete from there.
              </p>
              <Button
                variant="outline"
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                onClick={async () => {
                  if (!window.confirm(`Archive ${batch.name}? All upcoming sessions will be cancelled and the batch will disappear from learner views.`)) return;
                  try {
                    const res = await api.post(`/api/batches/${batchId}/archive`);
                    toast.success(res.data?.message || 'Batch archived');
                    navigate('/admin/batches');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to archive');
                  }
                }}
                data-testid="archive-batch-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />Archive batch
              </Button>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Cancel Session Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(o) => setCancelDialog({ open: o, session: null, note: '', creditsToDeduct: 0 })}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Cancel Session</DialogTitle></DialogHeader>
          {cancelDialog.session && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-muted/50">
                <p className="text-sm flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  {new Date(cancelDialog.session.scheduled_date + 'T00:00:00').toLocaleDateString('en', { weekday: 'short', day: '2-digit', month: 'short' })} · {formatTime(cancelDialog.session.scheduled_time)} - {getEndTime(cancelDialog.session.scheduled_time, cancelDialog.session.duration_minutes)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Add Note</label>
                <Textarea value={cancelDialog.note} onChange={e => setCancelDialog(prev => ({...prev, note: e.target.value}))} rows={3} placeholder="Reason for cancellation..." />
              </div>
              {batch?.batch_type === 'one_on_one' && (
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Deduct 1 credit?</label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="credits_deduct" value="0"
                        checked={String(cancelDialog.creditsToDeduct) === '0'}
                        onChange={() => setCancelDialog(prev => ({...prev, creditsToDeduct: 0}))}
                        data-testid="cancel-credits-deduct-0"
                      />
                      <span className="text-sm">No (full refund)</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="credits_deduct" value="1"
                        checked={String(cancelDialog.creditsToDeduct) === '1'}
                        onChange={() => setCancelDialog(prev => ({...prev, creditsToDeduct: 1}))}
                        data-testid="cancel-credits-deduct-1"
                      />
                      <span className="text-sm">Yes (deduct 1 credit)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog({ open: false, session: null, note: '', creditsToDeduct: 0 })}>Close</Button>
            <Button variant="destructive" onClick={handleCancel} data-testid="confirm-cancel-session-btn">Cancel Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Sessions Dialog with Scope */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => setDeleteDialog({ open: o, session: null, scope: 'this_session' })}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Delete sessions</DialogTitle></DialogHeader>
          {deleteDialog.session && (
            <div className="space-y-4">
              {['this_session', 'all_upcoming', 'same_day', 'same_day_time', 'same_time', 'same_instructor'].map(scope => {
                const dayName = new Date(deleteDialog.session.scheduled_date + 'T00:00:00').toLocaleDateString('en', { weekday: 'long' });
                const labels = {
                  this_session: 'This session',
                  all_upcoming: 'This and all upcoming sessions',
                  same_day: `This and all upcoming sessions on ${dayName}`,
                  same_day_time: `This and all upcoming sessions on ${dayName}, ${formatTime(deleteDialog.session.scheduled_time)}`,
                  same_time: `This and all upcoming sessions at ${formatTime(deleteDialog.session.scheduled_time)} (any day)`,
                  same_instructor: `This and all upcoming sessions with ${deleteDialog.session.instructor_name || 'Instructor'}`,
                };
                return (
                  <label key={scope} className="flex items-center gap-3 cursor-pointer">
                    <input type="radio" name="delete-scope" checked={deleteDialog.scope === scope} onChange={() => setDeleteDialog(prev => ({...prev, scope}))} className="w-4 h-4" />
                    <span className="text-sm">{labels[scope]}</span>
                  </label>
                );
              })}
              <p className="text-xs text-rose-600">Please note in case you delete session(s), all recordings associated with them will also be permanently deleted. This action will impact the current batch only.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, session: null, scope: 'this_session' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Scope Modal */}
      <BulkEditScopeModal
        session={editScopeSession}
        open={!!editScopeSession}
        onClose={() => setEditScopeSession(null)}
        onSelect={(scope) => {
          const s = editScopeSession;
          setEditScopeSession(null);
          navigate(`/admin/batches/${batchId}/edit-session?session=${s.id}&scope=${scope}`);
        }}
      />

      {/* Attendance Dialog */}
      <Dialog open={attendanceDialog.open} onOpenChange={(o) => setAttendanceDialog({ open: o, session: null, data: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-xl">Session Attendance</DialogTitle>
              {attendanceDialog.session?.status === 'live' && (
                <Button variant="outline" size="sm" onClick={() => viewAttendance(attendanceDialog.session)}>
                  <RefreshCw className="h-3 w-3 mr-1" />Refresh
                </Button>
              )}
            </div>
          </DialogHeader>
          {attendanceDialog.data ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{attendanceDialog.data.participants_count} participants</Badge>
                {attendanceDialog.session?.status === 'live' && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse mr-1 inline-block" />Live</Badge>}
              </div>
              {attendanceDialog.data.live_fetch_error?.startsWith('missing_scope') && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="scope-missing-hint">
                  <strong>Live attendance needs a Zoom scope.</strong> In your Zoom Marketplace S2S OAuth app,
                  add <code className="px-1 rounded bg-white">dashboard:read:list_meeting_participants:admin</code>
                  — then live participants will appear here automatically. Webhook events (meeting.participant_joined / left)
                  can also fill this list; verify the webhook URL is <code className="px-1 rounded bg-white">{`${window.location.origin}/api/zoom/webhook`}</code>.
                </div>
              )}
              {attendanceDialog.data.attendance.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No attendance data yet</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Via</TableHead><TableHead>Joined</TableHead><TableHead>Left</TableHead><TableHead>Duration</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {attendanceDialog.data.attendance.map((att, i) => {
                      const joinTime = att.join_time ? new Date(att.join_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-';
                      const leaveTime = att.leave_time ? new Date(att.leave_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null;
                      const isStillIn = !att.leave_time && attendanceDialog.session?.status === 'live';
                      return (
                        <TableRow key={i}>
                          <TableCell className="flex items-center gap-2">
                            <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{getInitials(att.lms_user_name || att.user_name)}</AvatarFallback></Avatar>
                            <span>{att.lms_user_name || att.user_name}</span>
                            {isStillIn && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" title="In session" />}
                          </TableCell>
                          <TableCell>
                            {att.joined_via === 'public' && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200" data-testid={`att-via-public-${i}`}>Zoom</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{joinTime}</TableCell>
                          <TableCell className="text-xs">{isStillIn ? <span className="text-emerald-600 font-medium">In session</span> : (leaveTime || '-')}</TableCell>
                          <TableCell className="text-xs">{att.duration_minutes ? `${att.duration_minutes} min` : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          ) : <p>Loading...</p>}
        </DialogContent>
      </Dialog>

      {/* Credit Management Modal */}
      <Dialog open={creditDialog.open} onOpenChange={(o) => setCreditDialog({ open: o })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Manage Credits — {batch?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Current Balance */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-primary/5">
              <div>
                <p className="text-xs text-muted-foreground">Current Balance</p>
                <p className="text-2xl font-semibold">{batch?.credits_remaining || 0} / {batch?.credits_total || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Consumed</p>
                <p className="text-lg font-semibold">{batch?.credits_consumed || 0}</p>
              </div>
            </div>
            
            {/* Add/Deduct */}
            <div className="flex gap-2">
              <Button variant={creditAction === 'add' ? 'default' : 'outline'} size="sm" onClick={() => setCreditAction('add')}>Add Credits</Button>
              <Button variant={creditAction === 'deduct' ? 'default' : 'outline'} size="sm" onClick={() => setCreditAction('deduct')}>Deduct Credits</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Amount</label>
                <Input type="number" min="1" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} placeholder="e.g. 5" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Note</label>
                <Input value={creditNote} onChange={e => setCreditNote(e.target.value)} placeholder="Reason for adjustment" />
              </div>
            </div>
            <Button className="w-full" onClick={async () => {
              if (!creditAmount || !creditNote) { toast.error('Amount and note required'); return; }
              const amt = creditAction === 'add' ? parseInt(creditAmount) : -parseInt(creditAmount);
              try {
                await api.post('/api/credits/adjust', { batch_id: batchId, learner_id: batch?.learner_id || '', amount: amt, note: creditNote });
                toast.success(`${Math.abs(amt)} credits ${creditAction === 'add' ? 'added' : 'deducted'}`);
                setCreditAmount(''); setCreditNote('');
                fetchData();
                const hist = await api.get(`/api/credits/${batchId}/history`);
                setCreditHistory(hist.data.history || []);
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
            }}>{creditAction === 'add' ? 'Add' : 'Deduct'} Credits</Button>
            
            {/* History */}
            {creditHistory.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Credit History</h4>
                <div className="max-h-[200px] overflow-y-auto space-y-1.5 scrollbar-thin">
                  {creditHistory.map((h, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg border text-sm">
                      <div>
                        <span className={`font-semibold ${h.amount > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{h.amount > 0 ? '+' : ''}{h.amount}</span>
                        <span className="text-muted-foreground ml-2">{h.note}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(h.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Learner Modal */}
      <Dialog open={learnerModal} onOpenChange={setLearnerModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Learners — {batch?.name}</DialogTitle>
            <p className="text-sm text-muted-foreground">{batch?.learners?.length || 0} learners enrolled</p>
          </DialogHeader>
          <div className="space-y-2">
            {batch?.learners?.length > 0 ? batch.learners.map(l => (
              <button key={l.id} onClick={() => { setLearnerModal(false); setDetailUserId(l.id); }}
                className="flex items-center gap-3 w-full p-3 rounded-xl border text-left hover:bg-muted/40 transition-colors">
                <Avatar className="h-8 w-8"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{getInitials(l.name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{l.email}</p>
                </div>
                {batch?.course_type === 'postnatal' ? (
                  l.pregnancy_profile?.delivery_mode ? (
                    <Badge
                      variant="outline"
                      className={`shrink-0 ${(l.pregnancy_profile.delivery_mode === 'vaginal' || l.pregnancy_profile.delivery_mode === 'natural')
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : 'bg-rose-50 text-rose-800 border-rose-200'}`}
                    >
                      {(l.pregnancy_profile.delivery_mode === 'vaginal' || l.pregnancy_profile.delivery_mode === 'natural') ? 'Vaginal birth' : 'C-section'}
                    </Badge>
                  ) : null
                ) : (
                  l.pregnancy_profile?.trimester && <Badge variant="outline" className="shrink-0">T{l.pregnancy_profile.trimester}</Badge>
                )}
              </button>
            )) : (
              <p className="text-center text-muted-foreground py-6">No learners enrolled</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* User Detail Modal (opened from learner list) */}
      <UserDetailModal userId={detailUserId} open={!!detailUserId} onClose={() => setDetailUserId(null)} onUpdate={fetchData} />

      {/* V14.5 — Bulk-assign course dialog */}
      <BulkAssignCourseDialog
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        onApplied={() => { setSelectedLearnerIds(new Set()); fetchData(); }}
        selectedLearners={
          (batch?.learners || []).filter(l => selectedLearnerIds.has(l.id))
        }
        batchCourseType={batch?.course_type}
      />

      {/* V14.5 — Bulk-assign batch dialog */}
      <BulkAssignBatchDialog
        open={bulkAssignBatchOpen}
        onClose={() => setBulkAssignBatchOpen(false)}
        onApplied={() => { setSelectedLearnerIds(new Set()); fetchData(); }}
        selectedLearners={
          (batch?.learners || []).filter(l => selectedLearnerIds.has(l.id))
        }
        currentBatchId={batchId}
      />

      {/* Session Detail Modal */}
      <SessionDetailModal session={selectedSession} open={!!selectedSession} onClose={() => setSelectedSession(null)} onUpdate={fetchData} />

      {/* Diagnostic Modal */}
      <Dialog open={!!diagnosticData} onOpenChange={() => setDiagnosticData(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              Session Diagnostics
            </DialogTitle>
          </DialogHeader>
          {diagnosticData && (
            <div className="space-y-4">
              {/* Session ID */}
              <div className="p-3 rounded-xl border bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Session ID</p>
                    <p className="text-sm font-mono font-medium" data-testid="diagnostic-session-id">{diagnosticData.id}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(diagnosticData.id); toast.success('Session ID copied!'); }} data-testid="copy-diagnostic-id">
                    <Copy className="h-3 w-3 mr-1" />Copy ID
                  </Button>
                </div>
              </div>

              {/* Session Info */}
              <div className="p-3 rounded-xl border">
                <p className="text-xs text-muted-foreground mb-2">Session Info</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Title:</span> {diagnosticData.title}</div>
                  <div><span className="text-muted-foreground">Batch:</span> {diagnosticData.batch_name}</div>
                  <div><span className="text-muted-foreground">Instructor:</span> {diagnosticData.instructor_name}</div>
                  <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className={`${getStatusColor(diagnosticData.status)} text-[10px]`}>{diagnosticData.status}</Badge></div>
                </div>
              </div>

              {/* Timeline */}
              {diagnosticData.timeline && (
                <div className="p-3 rounded-xl border">
                  <p className="text-xs text-muted-foreground mb-2">Timeline</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Scheduled:</span> {diagnosticData.timeline.scheduled}</div>
                    <div><span className="text-muted-foreground">Duration:</span> {diagnosticData.timeline.conducted_minutes ? `${Math.round(diagnosticData.timeline.conducted_minutes)}m actual` : `${diagnosticData.duration_minutes}m planned`}</div>
                    {diagnosticData.timeline.actual_start && <div><span className="text-muted-foreground">Started:</span> {diagnosticData.timeline.actual_start}</div>}
                    {diagnosticData.timeline.actual_end && <div><span className="text-muted-foreground">Ended:</span> {diagnosticData.timeline.actual_end}</div>}
                    {diagnosticData.timeline.auto_expired && <div className="col-span-2 text-amber-600 text-xs font-medium">Auto-expired (webhook not received)</div>}
                  </div>
                </div>
              )}

              {/* Recording Diagnostics */}
              {diagnosticData.recording_diagnostics && (
                <div className={`p-3 rounded-xl border ${diagnosticData.recording_diagnostics.status === 'available' ? 'border-emerald-200 bg-emerald-50/50' : diagnosticData.recording_diagnostics.status === 'failed' ? 'border-rose-200 bg-rose-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                  <p className="text-xs text-muted-foreground mb-2">Recording Status</p>
                  <Badge variant="outline" className={`text-xs mb-2 ${diagnosticData.recording_diagnostics.status === 'available' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : diagnosticData.recording_diagnostics.status === 'failed' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                    {diagnosticData.recording_diagnostics.status}
                  </Badge>
                  {diagnosticData.recording_diagnostics.issues?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {diagnosticData.recording_diagnostics.issues.map((issue, i) => (
                        <p key={i} className="text-xs text-rose-700 flex items-start gap-1">
                          <XCircle className="h-3 w-3 mt-0.5 shrink-0" />{issue}
                        </p>
                      ))}
                    </div>
                  )}
                  {diagnosticData.recording_diagnostics.signed_urls?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {diagnosticData.recording_diagnostics.signed_urls.map((u, i) => (
                        <div key={i} className="text-xs flex items-center gap-2">
                          <span className="text-muted-foreground capitalize">{u.view_type}:</span>
                          <span className="font-mono text-[10px] truncate">{u.bunny_guid}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Zoom Diagnostics */}
              {diagnosticData.zoom_diagnostics && (
                <div className="p-3 rounded-xl border" data-testid="zoom-diagnostics-block">
                  <p className="text-xs text-muted-foreground mb-2">Zoom</p>
                  <div className="text-sm space-y-1">
                    <div><span className="text-muted-foreground">Meeting ID:</span> {diagnosticData.zoom_diagnostics.meeting_id || 'Not linked'}</div>
                    <div><span className="text-muted-foreground">Status:</span> {diagnosticData.zoom_diagnostics.status}</div>
                    {diagnosticData.zoom_diagnostics.host_email && (
                      <div><span className="text-muted-foreground">Host email:</span> {diagnosticData.zoom_diagnostics.host_email}</div>
                    )}
                    {diagnosticData.zoom_diagnostics.instructor_email && (
                      <div><span className="text-muted-foreground">Instructor email:</span> {diagnosticData.zoom_diagnostics.instructor_email}</div>
                    )}
                    {diagnosticData.zoom_diagnostics.recording_files_count !== undefined && (
                      <div><span className="text-muted-foreground">Recording Files:</span> {diagnosticData.zoom_diagnostics.recording_files_count}</div>
                    )}
                    {diagnosticData.zoom_diagnostics.error && (
                      <p className="text-xs text-rose-600">{diagnosticData.zoom_diagnostics.error}</p>
                    )}
                    {/* V14.3 — Host/instructor mismatch warning + re-host action */}
                    {diagnosticData.zoom_diagnostics.host_mismatch && (
                      <div className="mt-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900" data-testid="zoom-host-mismatch-block">
                        <p className="text-xs text-amber-900 dark:text-amber-200">
                          ⚠ {diagnosticData.zoom_diagnostics.host_mismatch_message}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          data-testid="zoom-rehost-btn"
                          onClick={async () => {
                            try {
                              const sid = diagnosticData.session_id || diagnosticData.id;
                              const res = await api.post(`/api/sessions/${sid}/zoom/rehost`);
                              toast.success(`Re-hosted on ${res.data.new_host_email}`);
                              setDiagnosticData(null);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Re-host failed');
                            }
                          }}
                        >
                          Re-host on assigned instructor
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Attendance */}
              <div className="p-3 rounded-xl border">
                <p className="text-xs text-muted-foreground mb-2">Attendance ({diagnosticData.attendance_count || 0})</p>
                {diagnosticData.attendance_summary?.length > 0 ? (
                  <div className="space-y-1">
                    {diagnosticData.attendance_summary.map((a, i) => (
                      <div key={i} className="text-xs flex items-center gap-2">
                        <span className="font-medium">{a.name}</span>
                        <span className="text-muted-foreground">via {a.joined_via || 'unknown'}</span>
                        {a.duration_minutes && <span className="text-muted-foreground">{Math.round(a.duration_minutes)}m</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No attendance recorded</p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>Rating: {diagnosticData.avg_rating > 0 ? `${diagnosticData.avg_rating}/10` : 'None'}</span>
                <span>Feedback: {diagnosticData.feedback_count || 0}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* V14.3 — Booked-learners modal for group sessions */}
      <Dialog open={bookingsDialog.open} onOpenChange={(open) => setBookingsDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-lg" data-testid="bookings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {bookingsTab === 'waitlisted' ? 'Waitlist' : 'Booked Learners'}
            </DialogTitle>
            {bookingsDialog.session && (
              <p className="text-xs text-muted-foreground">
                {bookingsDialog.session.title} ·{' '}
                {bookingsDialog.session.scheduled_date} ·{' '}
                {formatTimeLocal(bookingsDialog.session.scheduled_time, bookingsDialog.session.scheduled_date)}
              </p>
            )}
          </DialogHeader>
          {bookingsDialog.loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : !bookingsDialog.data ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No data</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setBookingsTab('booked')}
                  data-testid="bookings-tab-booked"
                >
                  <Badge
                    variant={bookingsTab === 'booked' ? 'secondary' : 'outline'}
                    className={bookingsTab === 'booked' ? '' : 'opacity-60'}
                    data-testid="bookings-total-badge"
                  >
                    {bookingsDialog.data.total_booked} booked
                  </Badge>
                </button>
                {bookingsDialog.data.total_waitlisted > 0 && (
                  <button
                    type="button"
                    onClick={() => setBookingsTab('waitlisted')}
                    data-testid="bookings-tab-waitlisted"
                  >
                    <Badge
                      variant="outline"
                      className={`bg-amber-50 text-amber-800 border-amber-200 ${bookingsTab === 'waitlisted' ? 'ring-1 ring-amber-400' : 'opacity-70'}`}
                    >
                      {bookingsDialog.data.total_waitlisted} waitlisted
                    </Badge>
                  </button>
                )}
              </div>
              {bookingsTab === 'waitlisted' ? (
                <div className="space-y-2 max-h-80 overflow-auto" data-testid="waitlist-panel">
                  {(bookingsDialog.data.waitlisted || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">Nobody is on the waitlist for this class.</p>
                  ) : (
                    bookingsDialog.data.waitlisted.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        className="w-full flex items-center gap-3 p-2.5 rounded-xl border hover:bg-muted/40 text-left"
                        onClick={() => {
                          setDetailUserId(l.id);
                          setBookingsDialog((d) => ({ ...d, open: false }));
                        }}
                        data-testid={`waitlisted-learner-${l.id}`}
                      >
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-amber-100 text-amber-800 text-xs">
                            {(l.name || '?').slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            <span className="text-muted-foreground mr-1">#{l.position}</span>{l.name}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">{l.email}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {l.trimester && <Badge variant="outline" className="text-[9px]">T{l.trimester}</Badge>}
                          {l.already_booked && (
                            <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200" data-testid={`waitlist-conflict-${l.id}`}>
                              also booked — needs repair
                            </Badge>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              ) : bookingsDialog.data.booked.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No bookings yet for this session.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-auto">
                  {bookingsDialog.data.booked.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl border hover:bg-muted/40 text-left"
                      onClick={() => {
                        setDetailUserId(l.id);
                        setBookingsDialog((d) => ({ ...d, open: false }));
                      }}
                      data-testid={`booked-learner-${l.id}`}
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {(l.name || '?').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{l.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{l.email}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {l.trimester && (
                          <Badge variant="outline" className="text-[9px]">
                            T{l.trimester}
                          </Badge>
                        )}
                        {l.no_show_count > 0 && (
                          <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">
                            {l.no_show_count} no-show{l.no_show_count > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function BulkAssignCourseDialog({ open, onClose, onApplied, selectedLearners, batchCourseType }) {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load courses when dialog opens. Default-filter to batch's course_type so
  // admins don't accidentally pick a postnatal course for a prenatal batch
  // (would be skipped server-side anyway, but nicer UX to filter upfront).
  useEffect(() => {
    if (!open) return;
    setCoursesLoading(true);
    setSelectedCourseId('');
    setResult(null);
    api.get('/api/courses', {
      params: { status: 'published', course_type: batchCourseType, limit: 200 },
    })
      .then(r => setCourses(r.data?.courses || []))
      .catch(() => toast.error('Could not load courses'))
      .finally(() => setCoursesLoading(false));
  }, [open, batchCourseType]);

  const apply = async (dryRun) => {
    if (!selectedCourseId) {
      toast.error('Pick a course first');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/api/admin/ops/bulk-assign-course', {
        course_id: selectedCourseId,
        user_ids: selectedLearners.map(l => l.id),
        dry_run: dryRun,
      });
      setResult(res.data);
      const s = res.data.summary;
      toast.success(
        dryRun
          ? `Dry-run: ${s.assigned} would be assigned · ${s.skipped_already_assigned} already assigned · ${s.skipped_suspended} suspended-skipped · ${s.skipped_stage_mismatch} stage-mismatch`
          : `Done: ${s.assigned} assigned · ${s.skipped_already_assigned} already had it · ${s.skipped_suspended} suspended-skipped`,
        { duration: 7000 },
      );
      if (!dryRun) onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk-assign failed');
    } finally {
      setBusy(false);
    }
  };

  const selectedCourse = courses.find(c => c.id === selectedCourseId);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-rose-500" />
            Assign Course to {selectedLearners.length} learner{selectedLearners.length === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Course picker */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Course (matched to batch's course_type: <code>{batchCourseType}</code>)
            </label>
            {coursesLoading ? (
              <div className="flex items-center gap-2 p-3 border rounded-xl">
                <Loader2 className="h-4 w-4 animate-spin" />
                <p className="text-xs text-muted-foreground">Loading courses…</p>
              </div>
            ) : courses.length === 0 ? (
              <div className="p-3 border rounded-xl bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-900">
                  No published <strong>{batchCourseType}</strong> courses found.
                </p>
              </div>
            ) : (
              <div className="border rounded-xl divide-y max-h-64 overflow-auto" data-testid="bulk-course-list">
                {courses.map(c => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 ${selectedCourseId === c.id ? 'bg-rose-50' : ''}`}
                    data-testid={`bulk-course-option-${c.id}`}
                  >
                    <input
                      type="radio"
                      name="bulk-course"
                      value={c.id}
                      checked={selectedCourseId === c.id}
                      onChange={() => setSelectedCourseId(c.id)}
                      className="accent-rose-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.title}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                        <Badge variant="outline" className="text-[9px]">{c.course_type}</Badge>
                        <span>· {c.episode_count || 0} ep</span>
                        <span>· {c.learner_count || 0} learner{c.learner_count === 1 ? '' : 's'}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Selected learners preview */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Selected learners ({selectedLearners.length})
            </label>
            <div className="border rounded-xl p-2 max-h-32 overflow-auto bg-muted/30">
              <p className="text-[11px] text-muted-foreground">
                {selectedLearners.slice(0, 6).map(l => l.name).join(' · ')}
                {selectedLearners.length > 6 && ` · +${selectedLearners.length - 6} more`}
              </p>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Suspended learners are silently skipped. Stage-mismatched courses skipped too. Operation is idempotent.
            </p>
          </div>

          {/* Result panel */}
          {result && (
            <div className="border rounded-xl p-3 space-y-2" data-testid="bulk-assign-result">
              <p className="text-sm font-medium">
                {result.dry_run ? 'Dry-run result' : 'Applied'}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
                <Stat label="Candidates" value={result.summary.candidates} />
                <Stat label={result.dry_run ? 'Would assign' : 'Assigned'} value={result.summary.assigned} highlight="emerald" />
                <Stat label="Already had it" value={result.summary.skipped_already_assigned} />
                <Stat label="Suspended-skipped" value={result.summary.skipped_suspended} highlight={result.summary.skipped_suspended ? 'rose' : null} />
                <Stat label="Stage-mismatch" value={result.summary.skipped_stage_mismatch} />
                <Stat label="Errors" value={result.summary.errors} highlight={result.summary.errors ? 'rose' : null} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="bulk-assign-close">
            Close
          </Button>
          <Button
            variant="outline"
            disabled={!selectedCourseId || busy}
            onClick={() => apply(true)}
            data-testid="bulk-assign-dryrun-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Dry-run
          </Button>
          <Button
            disabled={!selectedCourseId || busy}
            onClick={() => {
              const n = selectedLearners.length;
              const courseName = selectedCourse?.title || 'this course';
              if (!window.confirm(`Assign "${courseName}" to ${n} learner${n === 1 ? '' : 's'}? Idempotent + audit-logged. Continue?`)) return;
              apply(false);
            }}
            data-testid="bulk-assign-apply-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Stat({ label, value, highlight }) {
  const colorClass =
    highlight === 'emerald' ? 'text-emerald-700' :
    highlight === 'rose' ? 'text-rose-700' :
    'text-foreground';
  return (
    <div className="px-2 py-1.5 rounded-lg bg-muted/40">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-semibold ${colorClass}`}>{value}</p>
    </div>
  );
}



function BulkAssignBatchDialog({ open, onClose, onApplied, selectedLearners, currentBatchId }) {
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load all group batches when dialog opens. Exclude the current batch
  // (assigning learners to the batch they're already in is a no-op).
  useEffect(() => {
    if (!open) return;
    setBatchesLoading(true);
    setSelectedBatchId('');
    setResult(null);
    api.get('/api/batches', { params: { batch_type: 'group', status: 'active', limit: 200 } })
      .then(r => {
        const items = (r.data?.batches || r.data || [])
          .filter(b => b.id !== currentBatchId);
        setBatches(items);
      })
      .catch(() => toast.error('Could not load batches'))
      .finally(() => setBatchesLoading(false));
  }, [open, currentBatchId]);

  const apply = async (dryRun) => {
    if (!selectedBatchId) {
      toast.error('Pick a target batch first');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/api/admin/ops/bulk-assign-batch', {
        target_batch_id: selectedBatchId,
        user_ids: selectedLearners.map(l => l.id),
        dry_run: dryRun,
      });
      setResult(res.data);
      const s = res.data.summary;
      toast.success(
        dryRun
          ? `Dry-run: ${s.added} would be added · ${s.skipped_already_member} already members · ${s.skipped_suspended} suspended-skipped`
          : `Done: ${s.added} added · ${s.skipped_already_member} already members · ${s.skipped_suspended} suspended-skipped`,
        { duration: 7000 },
      );
      if (!dryRun) onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk-assign failed');
    } finally {
      setBusy(false);
    }
  };

  const selectedBatch = batches.find(b => b.id === selectedBatchId);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-assign-batch-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Users className="h-5 w-5 text-rose-500" />
            Add {selectedLearners.length} learner{selectedLearners.length === 1 ? '' : 's'} to another batch
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Batch picker */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Target batch (group batches only — 1:1 batches are auto-managed by booking flow)
            </label>
            {batchesLoading ? (
              <div className="flex items-center gap-2 p-3 border rounded-xl">
                <Loader2 className="h-4 w-4 animate-spin" />
                <p className="text-xs text-muted-foreground">Loading batches…</p>
              </div>
            ) : batches.length === 0 ? (
              <div className="p-3 border rounded-xl bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-900">No other group batches found.</p>
              </div>
            ) : (
              <div className="border rounded-xl divide-y max-h-64 overflow-auto" data-testid="bulk-batch-list">
                {batches.map(b => (
                  <label
                    key={b.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 ${selectedBatchId === b.id ? 'bg-rose-50' : ''}`}
                    data-testid={`bulk-batch-option-${b.id}`}
                  >
                    <input
                      type="radio"
                      name="bulk-batch"
                      value={b.id}
                      checked={selectedBatchId === b.id}
                      onChange={() => setSelectedBatchId(b.id)}
                      className="accent-rose-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{b.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                        <Badge variant="outline" className="text-[9px]">{b.course_type}</Badge>
                        <span>· {(b.learner_ids?.length ?? b.learner_count ?? 0)} learner{(b.learner_ids?.length ?? b.learner_count ?? 0) === 1 ? '' : 's'}</span>
                        <span>· max {b.max_capacity || '—'}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Selected learners preview */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Selected learners ({selectedLearners.length})
            </label>
            <div className="border rounded-xl p-2 max-h-32 overflow-auto bg-muted/30">
              <p className="text-[11px] text-muted-foreground">
                {selectedLearners.slice(0, 6).map(l => l.name).join(' · ')}
                {selectedLearners.length > 6 && ` · +${selectedLearners.length - 6} more`}
              </p>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Suspended learners are silently skipped. Already-members are skipped. Operation is idempotent. Batch unlock_rule still gates session access.
            </p>
          </div>

          {/* Result panel */}
          {result && (
            <div className="border rounded-xl p-3 space-y-2" data-testid="bulk-assign-batch-result">
              <p className="text-sm font-medium">{result.dry_run ? 'Dry-run result' : 'Applied'}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <Stat label="Candidates" value={result.summary.candidates} />
                <Stat label={result.dry_run ? 'Would add' : 'Added'} value={result.summary.added} highlight="emerald" />
                <Stat label="Already members" value={result.summary.skipped_already_member} />
                <Stat label="Suspended-skipped" value={result.summary.skipped_suspended} highlight={result.summary.skipped_suspended ? 'rose' : null} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="bulk-assign-batch-close">
            Close
          </Button>
          <Button
            variant="outline"
            disabled={!selectedBatchId || busy}
            onClick={() => apply(true)}
            data-testid="bulk-assign-batch-dryrun-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Dry-run
          </Button>
          <Button
            disabled={!selectedBatchId || busy}
            onClick={() => {
              const n = selectedLearners.length;
              const batchName = selectedBatch?.name || 'this batch';
              if (!window.confirm(`Add ${n} learner${n === 1 ? '' : 's'} to "${batchName}"? Idempotent + audit-logged. Continue?`)) return;
              apply(false);
            }}
            data-testid="bulk-assign-batch-apply-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

